import React, { useEffect } from 'react';

import { StyledEngineProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { ToastContainer } from 'react-toastify';
import Hotjar from '@hotjar/browser';
import { ConfigCatProvider, createConsoleLogger, LogLevel, useFeatureFlag } from 'configcat-react';

import 'react-toastify/dist/ReactToastify.css';

import { ApplicationEnvironments } from 'enums/common.enums';
import AppRoutes from 'routes';
import AppRoutesPreQualificationV2 from 'prequalificationV2/routes';
import store, { persistor } from 'store';
import storePrequalificationV2, { persistor as persistorPrequalificationV2 } from 'prequalificationV2/store';
import './index.scss';
import NotificationCloseButton from 'components/Buttons/NotificationCloseButton/NotificationCloseButton';
import styles from 'pages/Main/Main.module.scss';
import { CONFIG_CAT_SDK_KEY, ENVIRONMENT, IS_HOTJAR_ENABLED } from 'constants/constants';
import { IGNORED_SENTRY_ERRORS, SENSITIVE_HEADERS } from 'constants/constants.sentry';
import { convertStringToBoolean } from 'utils/stringUtils';
import { FeatureFlag } from 'enums/featureFlag.enums';
import Loader from 'components/Loader';

const tagManagerArgs = {
  gtmId: `${process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID}`,
};

TagManager.initialize(tagManagerArgs);

const initHotJar = () => {
  if (!IS_HOTJAR_ENABLED) {
    return;
  }

  const hotjarVersion = 6;
  const siteId = +process.env.REACT_APP_HOTJAR_ID!;

  Hotjar.init(siteId, hotjarVersion);
};

const initSentry = () => {
  if (![ApplicationEnvironments.Production, ApplicationEnvironments.Testing].includes(ENVIRONMENT)) {
    return;
  }

  Sentry.init({
    environment: ENVIRONMENT,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    tracesSampleRate: 0.1,
    ignoreErrors: IGNORED_SENTRY_ERRORS,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Replaces the deprecated `sendDefaultPii: false`. Granular, PII-safe collection:
    // no auto user/IP (we set a non-PII borrowerId manually), no cookies, no request/response
    // bodies (they carry SSN/DOB/banking), and the auth header stripped.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpBodies: [],
      httpHeaders: {
        request: { deny: SENSITIVE_HEADERS },
        response: { deny: SENSITIVE_HEADERS },
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        blockAllMedia: true,
        maskAllText: true,
        maskAllInputs: true,
      }),
    ],
  });
};

initHotJar();
initSentry();

const App = () => {
  const isCachedNewPrequalificationEnabled = convertStringToBoolean(
    localStorage.getItem(FeatureFlag.IsNewPrequalificationEnabled),
  );
  const { value: isNewPrequalificationEnabled, loading } = useFeatureFlag(
    FeatureFlag.IsNewPrequalificationEnabled,
    false,
  );

  // Determine if new flow should be enabled
  // - If cachedFlag is explicitly true/false, use that value
  // - Otherwise fall back to the ConfigCat feature flag value
  const isNewFlowEnabled = isCachedNewPrequalificationEnabled ?? isNewPrequalificationEnabled;

  useEffect(() => {
    if (loading) {
      return;
    }

    localStorage.setItem(FeatureFlag.IsNewPrequalificationEnabled, String(isNewFlowEnabled));
  }, [loading, isNewFlowEnabled]);

  if (loading) {
    return <Loader />;
  }

  const [currentStore, currentPersistor, CurrentAppRoutes] = isNewFlowEnabled
    ? [storePrequalificationV2, persistorPrequalificationV2, AppRoutesPreQualificationV2]
    : [store, persistor, AppRoutes];

  return (
    <Provider store={currentStore}>
      <PersistGate loading={null} persistor={currentPersistor}>
        <BrowserRouter>
          <StyledEngineProvider injectFirst>
            <CurrentAppRoutes />
            <ToastContainer closeButton={NotificationCloseButton} className={styles.toastContainer} limit={3} />
          </StyledEngineProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
};

ReactDOM.render(
  <ConfigCatProvider sdkKey={CONFIG_CAT_SDK_KEY} options={{ logger: createConsoleLogger(LogLevel.Info) }}>
    <App />
  </ConfigCatProvider>,
  document.getElementById('root'),
);
